import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

/**
 * El vocabulario de la cirugía (services/vocabulario_cirugia.py), solo
 * admin: las palabras con las que la casa decide si una cirugía deja herida,
 * qué productos de la pauta son solo con herida (y su aviso y su
 * aclaración) y qué tipo de cita describe una nota. Cada clínica pone las
 * suyas; sin ellas nada se inventa y Configuración avisa de que faltan.
 *
 * Los campos (etiqueta, ayuda, tipo, grupo) los manda el servidor: esta
 * pantalla solo los pinta. Una lista es un término por línea; entre barras
 * («/…/») es una expresión regular. El cambio queda en Control (rastro).
 */
const ETIQUETAS_TIPOS = {
  completa: 'Las dos partes',
  completa_anadido: 'Las dos partes + añadido',
  una_parte: 'Una parte',
  una_parte_anadido: 'Una parte + añadido',
};

function aTexto(valor) {
  return Array.isArray(valor) ? valor.join('\n') : String(valor || '');
}

function aLista(texto) {
  return String(texto || '').split('\n').map((t) => t.trim()).filter(Boolean);
}

export default function VocabularioCirugiaPanel() {
  const [campos, setCampos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [valores, setValores] = useState({});
  const [meta, setMeta] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/api/vocabulario-cirugia');
      const lista = Array.isArray(data?.campos) ? data.campos : [];
      setCampos(lista);
      setTipos(Array.isArray(data?.tipos) ? data.tipos : Object.keys(ETIQUETAS_TIPOS));
      const voc = data?.vocabulario || {};
      setValores(Object.fromEntries(lista.map((c) => [
        c.clave, c.tipo === 'tipos' ? { ...(voc[c.clave] || {}) } : aTexto(voc[c.clave]),
      ])));
      setMeta({ updated_at: data?.updated_at, updated_by_nombre: data?.updated_by_nombre });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se ha podido cargar el vocabulario');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const grupos = useMemo(() => {
    const salida = [];
    for (const c of campos) {
      let g = salida.find((x) => x.grupo === c.grupo);
      if (!g) {
        g = { grupo: c.grupo, nombre: c.grupo_nombre || c.grupo, campos: [] };
        salida.push(g);
      }
      g.campos.push(c);
    }
    return salida;
  }, [campos]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const vocabulario = Object.fromEntries(campos.map((c) => {
        const v = valores[c.clave];
        if (c.tipo === 'tipos') return [c.clave, v || {}];
        if (c.tipo === 'texto') return [c.clave, String(v || '')];
        return [c.clave, aLista(v)];
      }));
      await api.put('/api/vocabulario-cirugia', { vocabulario });
      toast.success('Vocabulario guardado');
      await cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se ha podido guardar el vocabulario');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (
      <div className="cjo-card p-6 text-sm text-muted-foreground flex items-center gap-2" data-testid="vocabulario-cargando">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando el vocabulario…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="vocabulario-cirugia">
      <div className="cjo-card p-4 text-sm text-[#13231E] space-y-1">
        <p>
          Las palabras con las que se decide si la cirugía deja herida, qué productos de la pauta
          solo se usan con herida y qué tipo de cita describe la nota. Un término por línea: una
          palabra o frase casa como palabras enteras, sin tildes ni mayúsculas; entre barras
          («/…/») es una expresión regular.
        </p>
        {meta.updated_at && (
          <p className="text-xs text-muted-foreground" data-testid="vocabulario-ultima-edicion">
            Último cambio: {String(meta.updated_at).slice(0, 10)}
            {meta.updated_by_nombre ? ` · ${meta.updated_by_nombre}` : ''}
          </p>
        )}
      </div>

      {grupos.map((g) => (
        <section key={g.grupo} className="cjo-card p-4 space-y-3" data-testid={`vocabulario-grupo-${g.grupo}`}>
          <h3 className="font-display text-base font-semibold text-[#13231E]">{g.nombre}</h3>
          <div className="grid gap-3 md:grid-cols-2">
            {g.campos.map((c) => (
              <div key={c.clave} className={c.tipo === 'texto' || c.tipo === 'tipos' ? 'md:col-span-2' : ''}>
                <Label htmlFor={`vocabulario-${c.clave}`}>{c.etiqueta}</Label>
                {c.ayuda && <p className="text-xs text-muted-foreground mt-0.5">{c.ayuda}</p>}
                {c.tipo === 'tipos' ? (
                  <div className="grid gap-2 sm:grid-cols-2 mt-1.5">
                    {tipos.map((t) => (
                      <label key={t} className="text-xs text-[#55677a]">
                        {ETIQUETAS_TIPOS[t] || t}
                        <Input
                          value={(valores[c.clave] || {})[t] || ''}
                          onChange={(e) => setValores((prev) => ({
                            ...prev, [c.clave]: { ...(prev[c.clave] || {}), [t]: e.target.value },
                          }))}
                          placeholder="código del tipo de cita"
                          className="mt-1 font-mono text-sm"
                          data-testid={`vocabulario-${c.clave}-${t}`}
                        />
                      </label>
                    ))}
                  </div>
                ) : (
                  <Textarea
                    id={`vocabulario-${c.clave}`}
                    value={valores[c.clave] || ''}
                    onChange={(e) => setValores((prev) => ({ ...prev, [c.clave]: e.target.value }))}
                    rows={c.tipo === 'texto' ? 3 : 4}
                    className="mt-1.5 text-sm"
                    data-testid={`vocabulario-${c.clave}`}
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="flex justify-end">
        <Button onClick={guardar} disabled={guardando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="vocabulario-guardar">
          {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar vocabulario
        </Button>
      </div>
    </div>
  );
}
